import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const VIEWPORT_PAD = 8;

function computeMenuPosition(triggerRect, menuHeight, menuWidth) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.max(menuWidth, triggerRect.width);
  let left = triggerRect.left;
  if (left + width > vw - VIEWPORT_PAD) {
    left = Math.max(VIEWPORT_PAD, vw - width - VIEWPORT_PAD);
  }
  if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;

  const spaceBelow = vh - triggerRect.bottom - VIEWPORT_PAD;
  const spaceAbove = triggerRect.top - VIEWPORT_PAD;
  let top;
  if (spaceBelow >= menuHeight || spaceBelow >= spaceAbove) {
    top = triggerRect.bottom + 6;
    if (top + menuHeight > vh - VIEWPORT_PAD) {
      top = Math.max(VIEWPORT_PAD, vh - menuHeight - VIEWPORT_PAD);
    }
  } else {
    top = triggerRect.top - menuHeight - 6;
    if (top < VIEWPORT_PAD) top = VIEWPORT_PAD;
  }
  return { top, left, width };
}

function FilterGlyph({ name }) {
  const common = {
    width: 15,
    height: 15,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };
  if (name === "building") {
    return (
      <svg {...common}>
        <path d="M3 21V8a2 2 0 0 1 2-2h6v15" />
        <path d="M13 21V4h6a2 2 0 0 1 2 2v15" />
        <path d="M7 10h.01M7 14h.01M17 10h.01M17 14h.01" />
      </svg>
    );
  }
  if (name === "status") {
    return (
      <svg {...common}>
        <path d="M22 12A10 10 0 1 1 12 2" />
        <path d="M12 6v6l4 2" />
      </svg>
    );
  }
  if (name === "type") {
    return (
      <svg {...common}>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    );
  }
  if (name === "group") {
    return (
      <svg {...common}>
        <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
      </svg>
    );
  }
  if (name === "sort") {
    return (
      <svg {...common}>
        <path d="M11 5h10M11 12h7M11 19h4M3 8l3-3 3 3M6 5v14" />
      </svg>
    );
  }
  return null;
}

function CheckGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export default function SummaryFilterSelect({
  id,
  label,
  value,
  onChange,
  options = [],
  icon = null,
  title,
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 180 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const optionRefs = useRef([]);
  const typeaheadRef = useRef({ buffer: "", timer: null });
  const generatedId = useId();
  const fieldId = id || generatedId;
  const listId = `${fieldId}-list`;
  const labelId = `${fieldId}-label`;

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || options[0] || { value: "", label: "" },
    [options, value]
  );

  const close = useCallback(() => setOpen(false), []);

  const reposition = useCallback(() => {
    const btn = triggerRef.current;
    const menu = menuRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuHeight = menu?.offsetHeight || Math.min(options.length, 8) * 36 + 12;
    const menuWidth = Math.max(rect.width, 180);
    setMenuPos(computeMenuPosition(rect, menuHeight, menuWidth));
  }, [options.length]);

  const openMenu = useCallback(() => {
    const btn = triggerRef.current;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setMenuPos(computeMenuPosition(rect, Math.min(options.length, 8) * 36 + 12, Math.max(rect.width, 180)));
    }
    setOpen(true);
  }, [options.length]);

  const commit = useCallback(
    (nextValue) => {
      if (String(nextValue) !== String(value)) onChange?.(nextValue);
      close();
      triggerRef.current?.focus();
    },
    [close, onChange, value]
  );

  const focusOptionIndex = useCallback((index) => {
    const refs = optionRefs.current.filter(Boolean);
    if (!refs.length) return;
    const i = Math.max(0, Math.min(index, refs.length - 1));
    refs[i]?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    reposition();
    const selectedIdx = options.findIndex((o) => String(o.value) === String(value));
    const t = window.setTimeout(() => focusOptionIndex(selectedIdx < 0 ? 0 : selectedIdx), 0);
    return () => window.clearTimeout(t);
  }, [open, reposition, options, value, focusOptionIndex]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (e) => {
      const t = e.target;
      if (menuRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      close();
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        close();
        triggerRef.current?.focus();
        return;
      }
      if (e.key === "Tab") {
        close();
        return;
      }
      const refs = optionRefs.current.filter(Boolean);
      if (!refs.length) return;
      const idx = refs.findIndex((el) => el === document.activeElement);
      if (e.key === "ArrowDown") {
        e.preventDefault();
        focusOptionIndex(idx < 0 ? 0 : (idx + 1) % refs.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        focusOptionIndex(idx < 0 ? refs.length - 1 : (idx - 1 + refs.length) % refs.length);
      } else if (e.key === "Home") {
        e.preventDefault();
        focusOptionIndex(0);
      } else if (e.key === "End") {
        e.preventDefault();
        focusOptionIndex(refs.length - 1);
      } else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const buf = `${typeaheadRef.current.buffer}${e.key}`.toLowerCase();
        typeaheadRef.current.buffer = buf;
        window.clearTimeout(typeaheadRef.current.timer);
        typeaheadRef.current.timer = window.setTimeout(() => {
          typeaheadRef.current.buffer = "";
        }, 500);
        const match = options.findIndex((o) => String(o.label).toLowerCase().startsWith(buf));
        if (match >= 0) focusOptionIndex(match);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, close, reposition, options, focusOptionIndex]);

  useEffect(() => () => window.clearTimeout(typeaheadRef.current.timer), []);

  return (
    <div className="summary-filter-field summary-toolbar-select">
      {label ? (
        <label id={labelId} htmlFor={fieldId}>
          {label}
        </label>
      ) : null}
      <button
        ref={triggerRef}
        type="button"
        id={fieldId}
        className={`summary-toolbar-select-trigger${open ? " is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-labelledby={label ? `${labelId} ${fieldId}-value` : undefined}
        title={title || selected.title || undefined}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={(e) => {
          if ((e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") && !open) {
            e.preventDefault();
            openMenu();
          }
        }}
      >
        {icon ? (
          <span className="summary-toolbar-select-icon">
            <FilterGlyph name={icon} />
          </span>
        ) : null}
        <span id={`${fieldId}-value`} className="summary-toolbar-select-value">{selected.label}</span>
        <span className={`summary-toolbar-select-chevron${open ? " is-open" : ""}`} aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>
      {open && typeof document !== "undefined"
        ? createPortal(
            <ul
              ref={menuRef}
              id={listId}
              className="summary-toolbar-menu"
              role="listbox"
              aria-labelledby={labelId}
              style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width }}
            >
              {options.map((option, i) => {
                const isSelected = String(option.value) === String(value);
                return (
                  <li key={option.value} role="none">
                    <button
                      ref={(el) => {
                        optionRefs.current[i] = el;
                      }}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={`summary-toolbar-option${isSelected ? " is-selected" : ""}`}
                      title={option.title || undefined}
                      onClick={() => commit(option.value)}
                    >
                      <span className="summary-toolbar-option-label">{option.label}</span>
                      {isSelected ? (
                        <span className="summary-toolbar-option-check">
                          <CheckGlyph />
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>,
            document.body
          )
        : null}
    </div>
  );
}
